/**
 * Input bindings: defaults, persistence, rebinding and human-readable labels.
 * Three-free (no DOM access — the device adapters own that).
 *
 * Keyboard bindings use `KeyboardEvent.code` (physical key position), NOT `.key`, so a player on
 * AZERTY or Dvorak keeps the same physical layout instead of having their bindings scrambled.
 */

const STORAGE_KEY = 'coi.bindings.v1';

/** The six buttons plus four directions, in a stable order. */
export const ACTIONS = Object.freeze([
  'up', 'down', 'left', 'right', 'lp', 'hp', 'lk', 'hk', 'sp', 'guard',
]);

/** Display names, used by the settings screen and CONTROLS.md. */
export const ACTION_LABELS = Object.freeze({
  up: 'Up', down: 'Down', left: 'Left', right: 'Right',
  lp: 'Light Punch (1)', hp: 'Heavy Punch (2)',
  lk: 'Light Kick (3)', hk: 'Heavy Kick (4)',
  sp: 'Special / Stance (5)', guard: 'Guard (G)',
});

/**
 * Default keyboard layout: both players on one keyboard.
 * P1 uses the left hand cluster, P2 the arrows and numpad, so two people can share a desk.
 */
export const DEFAULT_KEYBOARD = Object.freeze([
  Object.freeze({
    up: 'KeyW', down: 'KeyS', left: 'KeyA', right: 'KeyD',
    lp: 'KeyF', hp: 'KeyG', lk: 'KeyV', hk: 'KeyB',
    sp: 'ShiftLeft', guard: 'Space',
  }),
  Object.freeze({
    up: 'ArrowUp', down: 'ArrowDown', left: 'ArrowLeft', right: 'ArrowRight',
    lp: 'Numpad4', hp: 'Numpad5', lk: 'Numpad1', hk: 'Numpad2',
    sp: 'Numpad7', guard: 'Numpad0',
  }),
]);

/**
 * Standard Gamepad mapping indices. Face buttons are laid out so punches sit on the top row and
 * kicks on the bottom, matching the 1/2 over 3/4 convention in the notation.
 *   2 = west, 3 = north, 0 = south, 1 = east, 4 = LB, 5 = RB, 6 = LT, 7 = RT
 */
export const DEFAULT_GAMEPAD = Object.freeze({
  lp: 2, hp: 3, lk: 0, hk: 1, sp: 4, guard: 5,
  /** Secondary bindings; either the shoulder or the trigger works. */
  spAlt: 6, guardAlt: 7,
  dpadUp: 12, dpadDown: 13, dpadLeft: 14, dpadRight: 15,
  axisX: 0, axisY: 1,
});

/**
 * Keys the game consumes and must therefore prevent the browser from acting on.
 * Space scrolls, arrows scroll, and F-keys / Tab must be left alone for the user.
 */
const PREVENT_DEFAULT_CODES = new Set([
  'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Numpad0', 'Numpad1', 'Numpad2', 'Numpad4', 'Numpad5', 'Numpad7',
]);

export class BindingSet {
  constructor() {
    /** @type {Array<Record<string, string>>} keyboard code per action, per player */
    this.keyboard = [{ ...DEFAULT_KEYBOARD[0] }, { ...DEFAULT_KEYBOARD[1] }];
    this.gamepad = { ...DEFAULT_GAMEPAD };
    /** Reverse lookup rebuilt on every change: code -> [{player, action}] */
    this._byCode = new Map();
    this.load();
    this.rebuildIndex();
  }

  rebuildIndex() {
    this._byCode.clear();
    for (let p = 0; p < this.keyboard.length; p++) {
      for (const action of ACTIONS) {
        const code = this.keyboard[p][action];
        if (!code) continue;
        let arr = this._byCode.get(code);
        if (!arr) {
          arr = [];
          this._byCode.set(code, arr);
        }
        arr.push({ player: p, action });
      }
    }
  }

  /** Every (player, action) a physical key maps to. Empty array when unbound. */
  lookup(code) {
    return this._byCode.get(code) || EMPTY;
  }

  isBound(code) {
    return this._byCode.has(code);
  }

  shouldPreventDefault(code) {
    return PREVENT_DEFAULT_CODES.has(code) || this._byCode.has(code);
  }

  /**
   * Rebinds one action. Returns a report rather than throwing, so the settings UI can show the
   * conflict and let the player decide.
   * @returns {{ ok: boolean, conflict: null | {player: number, action: string} }}
   */
  rebind(player, action, code, { allowConflict = false } = {}) {
    if (!ACTIONS.includes(action)) throw new Error(`Unknown action "${action}"`);
    const existing = this.lookup(code).find((e) => !(e.player === player && e.action === action));
    if (existing && !allowConflict) return { ok: false, conflict: existing };
    if (existing && allowConflict) this.keyboard[existing.player][existing.action] = '';
    this.keyboard[player][action] = code;
    this.rebuildIndex();
    this.save();
    return { ok: true, conflict: null };
  }

  /** Any action left unbound after a conflict resolution. */
  unboundActions() {
    const out = [];
    for (let p = 0; p < this.keyboard.length; p++) {
      for (const action of ACTIONS) {
        if (!this.keyboard[p][action]) out.push({ player: p, action });
      }
    }
    return out;
  }

  resetToDefaults(player = -1) {
    if (player < 0) {
      this.keyboard = [{ ...DEFAULT_KEYBOARD[0] }, { ...DEFAULT_KEYBOARD[1] }];
      this.gamepad = { ...DEFAULT_GAMEPAD };
    } else {
      this.keyboard[player] = { ...DEFAULT_KEYBOARD[player] };
    }
    this.rebuildIndex();
    this.save();
  }

  /**
   * Structured description for the settings screen and CONTROLS.md generation.
   * Returned as data rather than pre-formatted strings so the UI owns presentation.
   */
  describeBindings() {
    return {
      players: this.keyboard.map((map, player) => ({
        player,
        label: `Player ${player + 1}`,
        actions: ACTIONS.map((action) => ({
          action,
          label: ACTION_LABELS[action],
          code: map[action],
          keyLabel: keyLabel(map[action]),
        })),
      })),
      gamepad: {
        label: 'Gamepad (standard mapping)',
        actions: [
          { action: 'lp', label: ACTION_LABELS.lp, button: this.gamepad.lp, buttonLabel: 'West / X' },
          { action: 'hp', label: ACTION_LABELS.hp, button: this.gamepad.hp, buttonLabel: 'North / Y' },
          { action: 'lk', label: ACTION_LABELS.lk, button: this.gamepad.lk, buttonLabel: 'South / A' },
          { action: 'hk', label: ACTION_LABELS.hk, button: this.gamepad.hk, buttonLabel: 'East / B' },
          { action: 'sp', label: ACTION_LABELS.sp, button: this.gamepad.sp, buttonLabel: 'LB or LT' },
          { action: 'guard', label: ACTION_LABELS.guard, button: this.gamepad.guard, buttonLabel: 'RB or RT' },
        ],
        movement: 'D-pad or left stick',
      },
    };
  }

  load() {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed.keyboard)) {
        for (let p = 0; p < Math.min(2, parsed.keyboard.length); p++) {
          const src = parsed.keyboard[p];
          if (!src || typeof src !== 'object') continue;
          for (const action of ACTIONS) {
            // Drop unknown keys and non-string codes rather than trusting the save.
            if (typeof src[action] === 'string') this.keyboard[p][action] = src[action];
          }
        }
      }
      if (parsed.gamepad && typeof parsed.gamepad === 'object') {
        for (const key in DEFAULT_GAMEPAD) {
          if (typeof parsed.gamepad[key] === 'number') this.gamepad[key] = parsed.gamepad[key];
        }
      }
    } catch {
      // Corrupt save: keep defaults rather than failing to boot.
    }
  }

  save() {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        keyboard: this.keyboard,
        gamepad: this.gamepad,
      }));
    } catch {
      // Storage unavailable: bindings simply do not persist this session.
    }
  }
}

const EMPTY = Object.freeze([]);

/** Turns a KeyboardEvent.code into something a player recognises. */
export function keyLabel(code) {
  if (!code) return '—';
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Numpad')) return `Num ${code.slice(6)}`;
  if (code.startsWith('Arrow')) return `${code.slice(5)} Arrow`;
  const named = {
    Space: 'Space', ShiftLeft: 'L Shift', ShiftRight: 'R Shift',
    ControlLeft: 'L Ctrl', ControlRight: 'R Ctrl', AltLeft: 'L Alt', AltRight: 'R Alt',
    Enter: 'Enter', Escape: 'Esc', Tab: 'Tab', Backspace: 'Backspace',
    Comma: ',', Period: '.', Slash: '/', Semicolon: ';', Quote: "'",
    BracketLeft: '[', BracketRight: ']', Backslash: '\\', Minus: '-', Equal: '=',
  };
  return named[code] || code;
}
